// hooks/usePermissions.ts
import { useCallback, useEffect, useState, useRef } from "react";
import {
  doc,
  onSnapshot,
  Unsubscribe,
  collection,
  query,
  where,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { Permissions, TeamsStatus } from "ts-interfaces";
import {
  permissionsCache,
  incrementListenerCount,
  decrementListenerCount,
} from "./permissionsShared";
import {
  derivedPerms,
  toAccessResource,
  type AccessKind,
  type AccessUser,
  type ResourcePerm,
} from "../../schemas/access";
import { sharingV2Doc } from "./workspace/sharingV2";

export { sharingV2Doc };

export type AccessCheckType = "project" | "board" | "collection" | "ffeSchedule";

const ACCESS_KIND: Record<AccessCheckType, AccessKind> = {
  project: "project",
  board: "board",
  collection: "collection",
  ffeSchedule: "schedule",
};

// Firms the user is an active member of: the permissions doc's teams minus
// permissions.teamsInactive, as the Teams sharing rules count membership.
export const activeTeamIds = (permissions: Permissions | null | undefined): string[] => {
  const inactive = new Set(permissions?.teamsInactive ?? []);
  return [...(permissions?.teamsAdmin ?? []), ...(permissions?.teamsDesigner ?? [])].filter(
    (firm) => !inactive.has(firm),
  );
};

export const accessUserFromPermissions = (
  uid: string,
  permissions: Permissions | null | undefined,
): AccessUser => ({ id: uid, teams: activeTeamIds(permissions) });

export const resourcePermFromDoc = (
  permissions: Permissions | null | undefined,
  uid: string,
  type: AccessCheckType,
  doc: object,
): ResourcePerm => {
  const d = doc as { readers?: string[]; editors?: string[] };
  return derivedPerms(
    accessUserFromPermissions(uid, permissions),
    toAccessResource(ACCESS_KIND[type], doc),
    { readers: d.readers, editors: d.editors },
  );
};

// Cache for team status to avoid repeated queries
const teamStatusCache = new Map<string, boolean>();
const teamStatusPromiseCache = new Map<string, Promise<boolean>>();

// Function to clear team status cache.
// Entries are keyed per uid on the user's full firm set, so a firm-specific
// clear simply clears everything for that uid (over-clearing is safe — the
// next check re-queries).
export const clearTeamStatusCache = (uid?: string, _designFirmID?: string) => {
  if (uid) {
    Array.from(teamStatusCache.keys())
      .filter((k) => k.startsWith(`${uid}-`))
      .forEach((k) => {
        teamStatusCache.delete(k);
        teamStatusPromiseCache.delete(k);
      });
    Array.from(teamStatusPromiseCache.keys())
      .filter((k) => k.startsWith(`${uid}-`))
      .forEach((k) => teamStatusPromiseCache.delete(k));
  } else {
    teamStatusCache.clear();
    teamStatusPromiseCache.clear();
  }
};

export const hasAccessToResource = (
  permissions: Permissions | null,
  resourceID: string,
  accessType: "read" | "write",
  type: AccessCheckType,
  ownerUID: string,
  uid: string,
  _designFirmId?: string | null,
  // The Firestore doc, when the caller has it: the answer comes from it, as
  // the rules have it (a team doc's readers/editors; a Personal doc's owner).
  doc?: object | null,
) => {
  if (!uid || !resourceID) return false; // No access if no owner or user ID
  if (doc) {
    const perm = resourcePermFromDoc(permissions, uid, type, doc);
    return accessType === "write" ? perm === "read_write" : perm !== "no_access";
  }
  // Without the doc only ownership is certain. The permissions arrays grant
  // nothing since the Teams sharing cutover.
  return !!ownerUID && ownerUID === uid;
};

type AccessChecker = (
  id: string,
  accessType?: "read" | "write",
  ownerId?: string,
  doc?: object | null,
) => boolean;

interface UsePermissionsReturn {
  permissions: Permissions | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  hasProjectAccess: AccessChecker;
  hasBoardAccess: AccessChecker;
  hasCollectionAccess: AccessChecker;
}

// Hook for individual user permissions with optional real-time updates
export const usePermissions = (
  uid: string | null | undefined,
  db: any | null,
  useSubscription: boolean = false,
): UsePermissionsReturn => {
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const loadingRef = useRef(true);
  const [, forceUpdate] = useState({});
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const isInitialLoadRef = useRef(true);

  const setLoading = useCallback((value: boolean) => {
    if (loadingRef.current !== value) {
      loadingRef.current = value;
      forceUpdate({});
    }
  }, []);

  // Early return optimization for initial render
  useEffect(() => {
    if (!uid || !isInitialLoadRef.current) return;
    const cached = permissionsCache.get(uid);
    if (cached) {
      setPermissions(cached);
      setLoading(false);
    }
    isInitialLoadRef.current = false;
  }, [uid, setLoading]);

  const cleanupListener = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
      if (uid) decrementListenerCount(uid);
    }
  }, [uid]);

  // True when the user is an active roster member of ANY firm listed in
  // their permission arrays. Previously only the FIRST firm was checked,
  // so a user inactive in team #1 but active in team #2 read as inactive
  // and lost their entire permissions object app-wide.
  const checkTeamStatus = useCallback(
    async (permissionsData: Permissions): Promise<boolean> => {
      const firmIds = Array.from(
        new Set([
          ...(permissionsData.teamsDesigner || []),
          ...(permissionsData.teamsAdmin || []),
        ]),
      );
      if (!firmIds.length || !db) return false;
      // Keyed on the full firm set so a permissions change (joining/leaving
      // a team) naturally misses the cache — same convention as
      // activeFirmsCache in useWorkspaceContext.
      const cacheKey = `${uid}-${[...firmIds].sort().join(",")}`;

      const cached = teamStatusCache.get(cacheKey);
      if (cached !== undefined) return cached;

      let promise = teamStatusPromiseCache.get(cacheKey);
      if (promise) return promise;

      promise = (async () => {
        try {
          // One query for ALL the user's roster docs (one per firm).
          const q = query(collection(db, "teams"), where("uid", "==", uid));
          const snap = await getDocs(q);
          const firmSet = new Set(firmIds);
          const isActive = snap.docs.some((d) => {
            const data = d.data();
            return (
              firmSet.has(data.designFirmID) &&
              data.status !== TeamsStatus.INACTIVE
            );
          });
          teamStatusCache.set(cacheKey, isActive);
          return isActive;
        } catch {
          return false;
        } finally {
          teamStatusPromiseCache.delete(cacheKey);
        }
      })();

      teamStatusPromiseCache.set(cacheKey, promise);
      return promise;
    },
    [uid, db],
  );

  // Keep the permissions doc when the user is active SOMEWHERE (or is a
  // superuser); per-firm filtering is the workspace layer's job
  // (useWorkspaceContext.inactiveTeamIds).
  const processPermissions = useCallback(
    async (permissionsData: Permissions | null) => {
      const isSuperuser = permissionsData?.teamsSuperuser === true;
      const isActive =
        permissionsData &&
        (isSuperuser || (await checkTeamStatus(permissionsData)));
      permissionsCache[isActive ? "set" : "delete"](
        uid!,
        isActive ? permissionsData : undefined,
      );
      setPermissions(isActive ? permissionsData : null);
    },
    [uid, checkTeamStatus],
  );

  const fetchPermissionsOnce = useCallback(async () => {
    if (
      permissionsCache.has(uid) &&
      Object.keys(permissionsCache.get(uid)).length > 0
    )
      return;
    if (!uid || !db) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "permissions", uid));
      await processPermissions(
        snap.exists() ? (snap.data() as Permissions) : null,
      );
    } catch (e) {
      console.log("Dang yo! " + e);
      setError(e instanceof Error ? e : new Error("Failed to fetch"));
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [uid, db, processPermissions, setLoading]);

  const setupPermissionListener = useCallback(async () => {
    if (!uid || !db) return (setPermissions(null), setLoading(false));

    setError(null);
    cleanupListener();

    try {
      unsubscribeRef.current = onSnapshot(
        doc(db, "permissions", uid),
        { source: "default" },
        async (docSnap) => {
          await processPermissions(
            docSnap.exists() ? (docSnap.data() as Permissions) : null,
          );
          setLoading(false);
        },
        (error) => {
          console.error(`Error listening to permissions for ${uid}:`, error);
          setError(error);
          setLoading(false);
        },
      );
      incrementListenerCount(uid);
    } catch (e) {
      console.error("Error setting up permission listener:", e);
      setError(
        e instanceof Error
          ? e
          : new Error("Failed to setup permission listener"),
      );
      setLoading(false);
    }
  }, [uid, db, cleanupListener, processPermissions]);

  useEffect(() => {
    useSubscription ? setupPermissionListener() : fetchPermissionsOnce();
    return () => useSubscription && cleanupListener();
  }, [useSubscription, setupPermissionListener, fetchPermissionsOnce]);

  const refetch = useCallback(
    () =>
      useSubscription ? setupPermissionListener() : fetchPermissionsOnce(),
    [useSubscription, setupPermissionListener, fetchPermissionsOnce],
  );

  const createAccessChecker = (type: "project" | "board" | "collection") =>
    useCallback(
      (
        id: string,
        accessType: "read" | "write" = "read",
        ownerId?: string,
        doc?: object | null,
      ): boolean => {
        return hasAccessToResource(
          permissions,
          id,
          accessType,
          type,
          ownerId || "",
          uid || "",
          undefined,
          doc,
        );
        // if (accessType === 'write' && ownerId && uid === ownerId) return true;
        // if (!permissions) return false;
        // console.log('make it here?');
        // console.log('permissions', permissions);
        // const key = `${type}${accessType === 'write' ? 'Write' : 'Read'}` as keyof Permissions;
        // const bb =  (permissions[key] as string[] | undefined)?.includes(id) || false;
        // if (bb != z ) {
        //     console.warn(`Access check mismatch for ${type} ${id} (${accessType}): expected ${bb}, got ${z}`);
        // }
        // return bb
      },
      [permissions, uid],
    );

  return {
    permissions,
    loading: loadingRef.current,
    error,
    refetch,
    hasProjectAccess: createAccessChecker("project"),
    hasBoardAccess: createAccessChecker("board"),
    hasCollectionAccess: createAccessChecker("collection"),
  };
};
